import { queryOptions, type QueryClient } from "@tanstack/react-query";
import type { IssueCommentService } from "@/services/issue-comment.service";
import type { WorkItemService } from "@/services/work-item.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * A work item's feeds (NG-2806): the reads behind its Activity panel and its
 * comments, on the work-item page, the Quick View and an event's page.
 *
 * - **History**, `[slug, uid, 'work-item-activity', id]`: the item's activity
 *   rows interleaved with its comments. Access-sensitive, and never fresh: it
 *   is re-read each time the panel opens, as the store it replaces did, while
 *   the cached rows keep showing. A history records writes from every surface
 *   (an event page's own service calls, other people, automation), and no
 *   writer list could name them all.
 * - **Comments**, `[slug, uid, 'work-item-comments', id]`: mutable. An event is
 *   a work item, so its page and its Quick View share one entry.
 *
 * Neither polls: the stores they replace never did. The comments are re-read on
 * a focus or a revisit once 60 s old. The route depends on the project: an item in a
 * project reads the project-scoped feed, and only a project-less one (an
 * app-managed event) reads the workspace-scoped one, which refuses an item that
 * has a project (NG-2894, NG-717). The project is not in the key: an item has
 * one project, and a move re-reads the record that names it.
 *
 * @writers `useWorkItemComments` (post, react) patches the comments and marks
 * the history stale; `IssueStore`'s property and date writers mark the history
 * stale (`markWorkItemActivityStale`).
 */

type ActivityReader = Pick<WorkItemService, "getWorkItemActivity" | "getWorkspaceWorkItemActivity">;
type CommentsReader = Pick<IssueCommentService, "listComments" | "listWorkspaceComments">;

const ACTIVITY_DOMAIN = "work-item-activity";
const COMMENTS_DOMAIN = "work-item-comments";

export function workItemActivityKey(scope: QueryScope, issueId: string) {
  return scopedKey(scope, ACTIVITY_DOMAIN, issueId);
}

export function workItemCommentsKey(scope: QueryScope, issueId: string) {
  return scopedKey(scope, COMMENTS_DOMAIN, issueId);
}

/** A work item's history, newest last as the API orders it. */
export function workItemActivityQuery(
  service: ActivityReader,
  scope: QueryScope,
  projectId: string | null,
  issueId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    // Re-read on every open (see above); what is cached shows meanwhile.
    staleTime: 0,
    queryKey: workItemActivityKey(scope, issueId),
    queryFn: scopedRead(
      (request) =>
        projectId
          ? service.getWorkItemActivity(scope.tenant, projectId, issueId, request)
          : service.getWorkspaceWorkItemActivity(scope.tenant, issueId, request),
      { retry: "safe-read" },
    ),
  });
}

/** A work item's comments, oldest first as the API orders them. */
export function workItemCommentsQuery(
  service: CommentsReader,
  scope: QueryScope,
  projectId: string | null,
  issueId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    refetchInterval: false,
    queryKey: workItemCommentsKey(scope, issueId),
    queryFn: scopedRead(
      (request) =>
        projectId
          ? service.listComments(scope.tenant, projectId, issueId, request)
          : service.listWorkspaceComments(scope.tenant, issueId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * Mark a work item's cached history stale: the Activity panel, if open,
 * re-reads it. For a write made outside React whose change the server logs
 * too (`IssueStore`'s property and date writers). Never rejects; a failed mark
 * is logged.
 */
export function markWorkItemActivityStale(
  client: QueryClient,
  scope: QueryScope,
  issueId: string,
): void {
  client
    .invalidateQueries({ queryKey: workItemActivityKey(scope, issueId), exact: true })
    .catch((error: unknown) => {
      console.warn("[work-item-feed] marking the history stale failed", {
        tenant: scope.tenant,
        issueId,
        error,
      });
    });
}
